import { useEffect, useMemo, useState } from "react";

import { t } from "i18next";

import { Box, Typography } from "@mui/material";

import { carDataAtom } from "@/src/state/carDataAtom";

import UnifiedImageCarousel from "@/src/components/organisms/UnifiedImageCarousel/UnifiedImageCarousel";

import {
  HOMEPAGE_FRONT_PAGE_CONTAINER_STYLES,
  HOMEPAGE_SECTION_TITLE_SX
} from "@/src/pages/consumer/HomePage/homePage.constants";
import HomePageCarouselArrow from "@/src/pages/consumer/HomePage/HomePageImageCarousel/HomePageCarouselArrow";
import HomePageCarouselCard from "@/src/pages/consumer/HomePage/HomePageImageCarousel/HomePageCarouselCard";
import {
  CAROUSEL_AUTOPLAY_INTERVAL_MS,
  CAROUSEL_RESPONSIVE,
  CarouselResponsiveConfig
} from "@/src/pages/consumer/HomePage/HomePageImageCarousel/homePageImageCarousel.constants";
import {
  carouselPlaceholderSx,
  carouselWrapperSx,
  sectionSx
} from "@/src/pages/consumer/HomePage/HomePageImageCarousel/homePageImageCarousel.styles";
import useCarouselArrowController from "@/src/pages/consumer/HomePage/HomePageImageCarousel/useCarouselArrowController";
import {
  CarData,
  fetchCarData
} from "@/src/pages/consumer/HomePage/homePageUtils";

import { useIsHydrating } from "@/src/hooks/useIsHydrating";
import { UnifiedCarouselImage } from "@/src/types/carousel";
import { mapCarDataToCarouselImage } from "@/src/utils/carouselImageUtils";

import { useAtom } from "jotai";

// Per-side slide padding; total visible gap between two adjacent cards equals
// 2 * CAROUSEL_SLIDE_GUTTER_PX. Kept small to tighten the layout.
const CAROUSEL_SLIDE_GUTTER_PX = 2;

const EMPTY_CAR_DATA: CarData[] = [];

const HomePageImageCarousel = () => {
  const [storedCarData, setCarData] = useAtom(carDataAtom);
  // The atom is persisted in localStorage and read on init, but the prerendered
  // HTML was built without it. Rendering the stored cars while hydrating is a
  // hydration mismatch, which makes React re-render the whole document and
  // drop the dark mode attribute from <html> until MUI sets it again. Render
  // what the HTML has while hydrating and swap the cars in right after.
  const isHydrating = useIsHydrating();
  const carData = isHydrating ? EMPTY_CAR_DATA : storedCarData;
  const { wrapperRef, triggerArrow } = useCarouselArrowController();
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    fetchCarData()
      .then((data: CarData[]) => {
        setCarData(data);
      })
      .catch((error) => {
        console.error("Error setting data:", error);
      })
      .finally(() => setHasFetched(true));
  }, []);

  const carouselImages = useMemo(() => {
    if (!carData) return [];
    return carData.map(mapCarDataToCarouselImage);
  }, [carData]);

  // Cap items-per-view by the actual number of images so that, when there are
  // fewer images than the default (e.g. 2 cars), the visible cards sit next to
  // each other without leaving an empty slot.
  const responsiveConfig = useMemo<CarouselResponsiveConfig>(() => {
    const count = carouselImages.length || 1;
    const cap = (items: number) => Math.max(1, Math.min(items, count));
    return {
      desktop: {
        ...CAROUSEL_RESPONSIVE.desktop,
        items: cap(CAROUSEL_RESPONSIVE.desktop.items)
      },
      tablet: {
        ...CAROUSEL_RESPONSIVE.tablet,
        items: cap(CAROUSEL_RESPONSIVE.tablet.items)
      },
      mobile: {
        ...CAROUSEL_RESPONSIVE.mobile,
        items: cap(CAROUSEL_RESPONSIVE.mobile.items)
      }
    };
  }, [carouselImages.length]);

  const renderImage = (_image: UnifiedCarouselImage, index: number) => {
    const car = carData?.[index];
    if (!car) return null;
    return <HomePageCarouselCard car={car} />;
  };

  const hasImages = Boolean(carData) && carouselImages.length > 0;

  // When exactly 2 images are shown, cap the carousel width so the two 360px
  // cards sit close together (~0.5rem gap) instead of being spread across the
  // full homepage container. With 1 or 3+ images, leave width unconstrained.
  const carouselMaxWidth = carouselImages.length === 2 ? "736px" : undefined;

  return (
    <Box sx={[HOMEPAGE_FRONT_PAGE_CONTAINER_STYLES, sectionSx]}>
      <Box>
        <Typography variant="h2" sx={HOMEPAGE_SECTION_TITLE_SX}>
          {t("consumer.homePage.recentlySoldTitle")}
        </Typography>
      </Box>
      {hasImages ? (
        <Box ref={wrapperRef} sx={carouselWrapperSx}>
          <UnifiedImageCarousel
            images={carouselImages}
            responsive={responsiveConfig}
            loop={true}
            autoplay={true}
            autoplayIntervalMs={CAROUSEL_AUTOPLAY_INTERVAL_MS}
            renderImage={renderImage}
            maxWidth={carouselMaxWidth}
            slideGutterPx={CAROUSEL_SLIDE_GUTTER_PX}
          />
          <HomePageCarouselArrow
            direction="left"
            label={t("common.previous") ?? "Previous"}
            onClick={() => triggerArrow("left")}
          />
          <HomePageCarouselArrow
            direction="right"
            label={t("common.next") ?? "Next"}
            onClick={() => triggerArrow("right")}
          />
        </Box>
      ) : (
        // Reserve the carousel's height until the cars are known so the
        // sections below don't jump down when it appears.
        !hasFetched && <Box aria-hidden sx={carouselPlaceholderSx} />
      )}
    </Box>
  );
};

export default HomePageImageCarousel;
